import { apiFetch, apiFetchEnvelope, type ListMeta } from "@core/http";
import type {
  Progress,
  ProgressDetail,
  ProgressIndicators,
  SupportingPurchase,
} from "@core/types";

export type {
  Progress,
  ProgressDetail,
  ProgressIndicators,
  SupportingPurchase,
};
export type ProgressStatus = Progress["status"];
export type ProgressStatusFilter = ProgressStatus | "all";
export type AccountFilter = "all" | "activated" | "not_activated";

export interface ProgressQuery {
  status: ProgressStatusFilter;
  account: AccountFilter;
  search: string;
  cursor?: string;
}

export interface ProgressPage {
  items: Progress[];
  indicators: ProgressIndicators;
  meta: ListMeta;
}

const base = "/v1/portal/grand-prize/progress";

export function progressSearchParams(query: ProgressQuery) {
  const params = new URLSearchParams();
  if (query.status !== "all") params.set("status", query.status);
  if (query.account !== "all")
    params.set("accountActivated", String(query.account === "activated"));
  if (query.search.trim()) params.set("search", query.search.trim());
  if (query.cursor) params.set("cursor", query.cursor);
  return params;
}

// `portalListProgress` entrega en una sola llamada la página y los cinco
// indicadores de cabecera (SPEC-209 §2): no hay una segunda llamada para los
// indicadores, ni acá ni en `progressQueries.ts`.
export async function listProgress(
  query: ProgressQuery,
): Promise<ProgressPage> {
  const params = progressSearchParams(query);
  const envelope = await apiFetchEnvelope<{
    items: Progress[];
    indicators: ProgressIndicators;
  }>(`${base}${params.size ? `?${params}` : ""}`);
  return {
    items: envelope.data.items,
    indicators: envelope.data.indicators,
    meta: envelope.meta ?? { next: null, total: envelope.data.items.length },
  };
}

export const getProgressDetail = (id: string) =>
  apiFetch<ProgressDetail>(`${base}/${encodeURIComponent(id)}`);

// SUB-001 paso 1: valida la meta (RN-13). El servidor puede responder `422
// GOAL_UNVALIDATED` si el avance ya bajó de la meta antes de esta llamada.
export const validateGoal = (id: string) =>
  apiFetch<Progress>(`${base}/${encodeURIComponent(id)}/validate`, {
    method: "POST",
  });

export interface DeliverPrizeInput {
  // PG-002: excedente reconocido a criterio de la administración, opcional,
  // entre 0 y el excedente real. Motivo obligatorio si es mayor que 0
  // (`REASON_REQUIRED`).
  initialProgress?: number;
  initialProgressReason?: string;
}
export interface DeliverPrizeResult {
  delivered?: Progress;
  newProgress?: Progress;
}

// SUB-001 paso 2: registra la entrega. Solo se ofrece con la meta ya
// validada; ante `422 GOAL_UNVALIDATED` no se reintenta (SPEC-209 §8).
export const deliverPrize = (id: string, input: DeliverPrizeInput) =>
  apiFetch<DeliverPrizeResult>(`${base}/${encodeURIComponent(id)}/deliver`, {
    method: "POST",
    body: input,
  });

// SPEC-209 §8, literal — un solo lugar para los cinco códigos de la
// pantalla, compartido por los tres modales.
// PLAN-009 §3: viaja como `codeMessages` a `reportError` (toast, tono
// error) en vez de resolver un texto para pintarlo en pantalla.
export const PROGRESS_ERROR_MESSAGES: Record<string, string> = {
  GOAL_UNVALIDATED: "La meta debe validarse antes de registrar la entrega",
  PRIZE_DELIVERED:
    "Este conteo tiene un premio entregado. La corrección debe tramitarse como excepción",
  OLD_DATE: "La fecha indicada tiene más de 30 días. ¿Continuar?",
  REASON_REQUIRED: "Indica el motivo del avance inicial reconocido",
  PROGRESS_REGRESSED: "El avance bajó de la meta y el estado volvió a En curso",
};
